import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { CheckinVerdict } from "@/types";

/**
 * Loads every *unresolved* damage verdict so surfaces like the host dashboard
 * and My trips can raise an alert instead of burying the dispute on the booking page.
 */
export function useFlaggedBookings() {
  const [flagged, setFlagged] = useState<Map<number, CheckinVerdict>>(new Map());
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("checkin_verdicts")
        .select("*")
        .eq("flagged", true)
        .eq("resolved", false);
      if (error) throw error;
      if (mounted.current) {
        const next = new Map<number, CheckinVerdict>();
        (data as CheckinVerdict[] | null)?.forEach((row) => {
          if (row?.booking_id) next.set(Number(row.booking_id), row);
        });
        setFlagged(next);
      }
    } catch {
      if (mounted.current) setFlagged(new Map());
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { flagged, loading, reload: load };
}